export default (survey) => {
  const isResume = isResumedSurvey(survey);

  const state = {
    validatedStepIndices: new Set(),
    visitedStepNames: new Set(),
    completedStepNames: new Set(),
    missingStepNames: new Set(),
  };

  seedFromSurvey(survey, state);

  let lastIndex1Based = resolveCurrentVisibleIndex1Based(survey);
  let lastPage = survey.currentPage;

  const render = () => {
    const nextIndex1Based = resolveCurrentVisibleIndex1Based(survey);
    const nextPage = survey.currentPage;
    const leftPage = lastPage;

    markVisitedFromPage(nextPage, state.visitedStepNames);
    if (leftPage && leftPage !== nextPage) {
      markLeftPageMissingState(leftPage, state.missingStepNames);
    }
    if (Number.isFinite(lastIndex1Based) && Number.isFinite(nextIndex1Based) && nextIndex1Based > lastIndex1Based) {
      state.validatedStepIndices.add(lastIndex1Based);
    }
    accumulateCompletedStepNamesFromSurvey(
      survey,
      state.completedStepNames,
      state.visitedStepNames,
      { isResume }
    );
    updateMissingStepNamesFromSurvey(
      survey,
      state.missingStepNames,
      state.validatedStepIndices,
      nextIndex1Based,
      state.visitedStepNames
    );
    persistBareMeta(survey, state);
    lastIndex1Based = nextIndex1Based;
    lastPage = nextPage;
    renderNavigation(survey, state);
  };
  render();
  survey.onCurrentPageChanged.add(render);

  document.addEventListener("top_stepper:navigate", (event) => {
    const index1Based = Number(event?.detail?.index);
    if (!Number.isFinite(index1Based) || index1Based < 1) return;
    const targetIndex = index1Based - 1;
    if (!canNavigateTo(survey, targetIndex, state.validatedStepIndices)) return;
    const targetPage = visiblePages(survey)[targetIndex];
    if (targetPage) survey.currentPage = targetPage;
  });
};

const REVIEW_PAGE_NAME = "Review";
const UNNAMED_PAGE_FALLBACK_TITLE = "Step";

function isResumedSurvey(survey) {
  try {
    if (Number.isFinite(survey?.currentPageNo) && survey.currentPageNo > 0) return true;

    const data = survey?.data;
    if (!data || typeof data !== "object") return false;

    // Ignore meta-ish keys that exist even on fresh surveys.
    const keys = Object.keys(data).filter((k) => k !== "__bare" && k !== "brand" && k !== "brand_name");
    return keys.length > 0;
  } catch (_) {
    return false;
  }
}

function seedFromSurvey(survey, state) {
  const currentIndex = resolveCurrentVisibleIndex1Based(survey);

  mergePersistedVisitedNames(survey, state.visitedStepNames);

  markVisitedUpToIndex(survey, currentIndex, state.visitedStepNames);
  accumulateCompletedStepNamesFromSurvey(survey, state.completedStepNames, state.visitedStepNames, {
    isResume: isResumedSurvey(survey),
  });
  updateMissingStepNamesFromSurvey(
    survey,
    state.missingStepNames,
    state.validatedStepIndices,
    currentIndex,
    state.visitedStepNames
  );
}

function renderNavigation(survey, state) {
  updateProgressBar(survey);
  dispatchTopStepperChange(
    survey,
    state.completedStepNames,
    state.missingStepNames,
    state.visitedStepNames
  );

  const ul = document.querySelector("#pageButtonsContainer ul");
  const template = getTemplate();
  if (!ul || !template) return;

  ul.innerHTML = "";
  const fragment = document.createDocumentFragment();

  visiblePages(survey).forEach((page, index) => {
    const li = buildPageButton(
      { survey, page, index, template },
      state.validatedStepIndices
    );
    fragment.appendChild(li);
  });

  ul.appendChild(fragment);
}

function dispatchTopStepperChange(
  survey,
  completedStepNames = new Set(),
  missingStepNames = new Set(),
  visitedStepNames = new Set()
) {
  // Bridge SurveyJS state → Preline top stepper (which is rendered from visible pages).
  const currentIndex = resolveCurrentVisibleIndex1Based(survey);
  // `visiblePageNames` is persisted server-side and should remain raw (names can be blank/undefined).
  // For DOM reconciliation we also send `visibleStepTitles`, which uses the same fallback as Rails.
  const visiblePageNames = visiblePages(survey).map((p) => p?.name);
  const visibleStepTitles = visiblePages(survey).map((p) => pageTitle(p));
  const completedIndices = computeCompletedIndices(survey, completedStepNames, visitedStepNames, {
    isResume: isResumedSurvey(survey),
  });
  const missingIndices = computeMissingIndices(survey, missingStepNames, visitedStepNames);

  document.dispatchEvent(
    new CustomEvent("top_stepper:change", {
      detail: { currentIndex, completedIndices, missingIndices, visiblePageNames, visibleStepTitles },
    })
  );
}

function markVisitedFromPage(page, visitedStepNames = new Set()) {
  const name = page?.name;
  if (!name || isReviewName(name)) return;
  visitedStepNames.add(name);
}

function markVisitedUpToIndex(survey, currentIndex1Based, visitedStepNames = new Set()) {
  const idx = Number(currentIndex1Based);
  if (!Number.isFinite(idx) || idx < 1) return;

  visiblePages(survey).forEach((page, i) => {
    const index1Based = i + 1;
    if (index1Based > idx) return;
    markVisitedFromPage(page, visitedStepNames);
  });
}

export function markLeftPageMissingState(leftPage, missingStepNames = new Set()) {
  const name = leftPage?.name;
  if (!name || isReviewName(name)) return;

  const hasErrors = !!leftPage?.hasErrors?.(true);
  if (hasErrors) {
    missingStepNames.add(name);
  } else {
    missingStepNames.delete(name);
  }
}

function resolveCurrentVisibleIndex1Based(survey) {
  return currentVisibleIndex0Based(survey) + 1;
}

function currentVisibleIndex0Based(survey) {
  const pages = visiblePages(survey);
  const visibleIndex = pages.indexOf(survey.currentPage);
  if (visibleIndex >= 0) return visibleIndex;
  // Defensive fallback; currentPageNo can include invisible pages.
  return Number.isFinite(survey?.currentPageNo) ? survey.currentPageNo : 0;
}

function mergePersistedVisitedNames(survey, visitedStepNames = new Set()) {
  try {
    const names = survey?.data?.__bare?.visitedPageNames;
    if (!Array.isArray(names)) return;
    names.forEach((n) => {
      if (typeof n === "string" && n.length > 0 && !isReviewName(n)) visitedStepNames.add(n);
    });
  } catch (_) {
    // ignore
  }
}

function persistBareMeta(survey, state) {
  try {
    if (!survey || typeof survey !== "object") return;
    const data = survey.data && typeof survey.data === "object" ? survey.data : (survey.data = {});
    const bare = data.__bare && typeof data.__bare === "object" ? data.__bare : (data.__bare = {});
    bare.visitedPageNames = Array.from(state.visitedStepNames);
  } catch (_) {
    // ignore
  }
}

function accumulateCompletedStepNamesFromSurvey(
  survey,
  completedStepNames = new Set(),
  visitedStepNames = new Set(),
  { isResume = false } = {}
) {
  visiblePages(survey).forEach((page) => {
    const name = page?.name;
    if (!name || isReviewName(name)) return;
    // Fresh sessions: only mark completed after visited.
    // Resumed sessions: allow completed state for "future" steps too (they may have been validated before).
    if (!isResume && !visitedStepNames.has(name)) return;
    if (page?.hasErrors?.(false)) return;
    completedStepNames.add(name);
  });
}

function updateMissingStepNamesFromSurvey(
  survey,
  missingStepNames = new Set(),
  validatedStepIndices = new Set(),
  currentIndex,
  visitedStepNames = new Set()
) {
  // Missing = has errors + eligible.
  //
  // Eligibility rules:
  // - current step: only mark missing after it has been validated (to avoid "missing" on first paint)
  // - past/future steps: if SurveyJS reports errors, show missing immediately
  // BUT: only for steps that have been visited.
  visiblePages(survey).forEach((page, i) => {
    const index = i + 1;
    const name = page?.name;
    if (!name || isReviewName(name)) return;
    if (!visitedStepNames.has(name)) {
      // Don't apply validation/state to unvisited steps.
      missingStepNames.delete(name);
      return;
    }

    const hasErrors = !!page?.hasErrors?.(false);
    if (!hasErrors) {
      missingStepNames.delete(name);
      return;
    }

    const eligible = index !== currentIndex || validatedStepIndices.has(index);
    if (eligible || missingStepNames.has(name)) missingStepNames.add(name);
  });
}

function computeMissingIndices(
  survey,
  missingStepNames = new Set(),
  visitedStepNames = new Set()
) {
  return visiblePages(survey)
    .map((page, index) => ({ page, index: index + 1 }))
    .filter(({ page }) => {
      if (isReviewName(page?.name)) return false;
      if (!visitedStepNames.has(page?.name)) return false;
      if (!missingStepNames.has(page?.name)) return false;
      return !!page?.hasErrors?.(false);
    })
    .map(({ index }) => index);
}

export function computeCompletedIndices(
  survey,
  completedStepNames = new Set(),
  visitedStepNames = new Set(),
  { isResume = false } = {}
) {
  return visiblePages(survey)
    .map((page, index) => ({ page, index: index + 1 }))
    .filter(({ page }) => {
      if (isReviewName(page?.name)) return false;
      if (!isResume && !visitedStepNames.has(page?.name)) return false;
      if (page?.hasErrors?.(false)) return false;
      return completedStepNames.has(page?.name);
    })
    .map(({ index }) => index);
}

function getTemplate() {
  const el = document.getElementById("pageButtonTemplate");
  if (!el) {
    return null;
  }
  return el.content;
}

function buildPageButton({ survey, page, index, template }, validatedStepIndices) {
  const clone = document.importNode(template, true);
  const button = clone.querySelector("button");
  const li = clone.querySelector("li");

  setStaticParts(clone, page, index);
  const status = getStatus({ survey, page, index });
  applyStatusClasses(li, status);
  applyErrorStyles(li, status);
  updateIcon(li, status);
  handleClick(button, survey, index, validatedStepIndices);

  return li;
}

function setStaticParts(clone, page, index) {
  clone.querySelector(".page-number").textContent = index + 1;
  clone.querySelector(".page-title").textContent = page.name;
}

function getStatus({ survey, page, index }) {
  const current = currentVisibleIndex0Based(survey);
  const isActive = current === index;
  const isPassed = current > index;
  const isReview = isReviewName(page?.name);
  const hasErrors = !!page?.hasErrors?.(false);
  const isComplete = !hasErrors && !isReview;
  return { isActive, isPassed, isComplete, hasErrors };
}

function applyStatusClasses(li, { isActive, isPassed, hasErrors }) {
  li.classList.remove("active", "passed", "completed");

  if (hasErrors) {
    li.classList.add("after:bg-amber-100");
    li.classList.remove("after:bg-chalk-600");
    li.classList.remove("after:bg-forest-500");
    li.classList.remove("after:bg-gray-200");
    return;
  }
  if (isActive) {
    li.classList.add("active");
    li.classList.add("passed", "after:bg-forest-500");
    li.classList.remove("after:bg-amber-100");
    li.classList.remove("after:bg-gray-200");
  }
  else if (isPassed) {
    li.classList.add("passed", "after:bg-forest-500");
    li.classList.remove("after:bg-amber-100");
    li.classList.remove("after:bg-gray-200");
  }
}

function applyErrorStyles(li, { hasErrors }) {
  if (!hasErrors) { return; }

  const titleEl = li.querySelector(".page-title");
  titleEl?.classList.remove(
    "text-forest-500"
  );
  titleEl?.classList.add("text-amber-600");

  const numberEl = li.querySelector(".page-number");
  numberEl?.classList.remove(
    "bg-chalk-600",
    "text-chalk-900",
    "passed:bg-forest-500",
    "active:bg-forest-700",
    "passed:text-white",
    "active:text-white",
    "text-white"
  );
  numberEl?.classList.add("bg-amber-100", "text-amber-600");
}

function handleClick(btn, survey, index, validatedStepIndices) {
  if (!btn) return;
  btn.addEventListener("click", () => {
    if (!canNavigateTo(survey, index, validatedStepIndices)) return;
    const page = visiblePages(survey)[index];
    if (page) survey.currentPage = page;
  });
}

function updateIcon(li, { isActive, hasErrors }) {
  if(!isActive) return;

  const bubble = li.querySelector(".page-number");
  if (!bubble) return;

  bubble.className = [
    "flex",
    "items-center",
    "justify-center",
    "size-7",
    "rounded-full",
  ].join(" ");

  bubble.innerHTML = pinSvg();

  if (hasErrors) {
    bubble.classList.add(
      "outline",
      "outline-1",
      "outline-amber-500",
      "bg-amber-100",
      "text-amber-600",
    );
  } else {
    bubble.classList.add(
      "outline",
      "outline-1",
      "outline-forest-600",
      "bg-forest-200",
      "text-mint-50",
    );
  }
}

function pinSvg() {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 49 50" class="w-6 h-6">' +
    '<path d="M24.7502 7.54004C17.5293 7.54004 11.6552 13.4262 11.6552 20.659C11.6552 31.4919 23.8782 41.8937 24.3988 42.3313C24.5003 42.4175 24.6257 42.46 24.7502 42.46C24.8735 42.46 24.9968 42.4186 25.0972 42.3356C25.6177 41.9057 37.8451 31.6829 37.8451 20.659C37.8451 13.4262 31.971 7.54004 24.7502 7.54004ZM24.7523 41.192C22.7433 39.3969 12.7464 29.9969 12.7464 20.659C12.7464 14.0264 18.1317 8.63129 24.7502 8.63129C31.3686 8.63129 36.7539 14.0264 36.7539 20.659C36.7539 30.1551 26.7668 39.4209 24.7523 41.192Z" fill="currentColor"/>' +
    '<path d="M24.7501 14.0879C21.1392 14.0879 18.2026 17.0244 18.2026 20.6354C18.2026 24.2463 21.1392 27.1829 24.7501 27.1829C28.3611 27.1829 31.2976 24.2463 31.2976 20.6354C31.2976 17.0244 28.3611 14.0879 24.7501 14.0879ZM24.7501 26.0916C21.7416 26.0916 19.2939 23.644 19.2939 20.6354C19.2939 17.6268 21.7416 15.1791 24.7501 15.1791C27.7587 15.1791 30.2064 17.6268 30.2064 20.6354C30.2064 23.644 27.7587 26.0916 24.7501 26.0916Z" fill="currentColor"/>' +
    '</svg>'
  );
}

function updateProgressBar(survey) {
  const progressBar = document.getElementById("progressBar");
  const innerProgressBar = progressBar?.firstElementChild;
  const labelText = document.getElementById("progressLabel");

  if (!progressBar || !innerProgressBar || !labelText) return;

  const percent = calcCompletionPercent(survey);

  progressBar.setAttribute("aria-valuenow", percent);
  innerProgressBar.style.width = `${percent}%`;
  labelText.textContent = `${percent}%`;
}

function calcCompletionPercent(survey) {
  const pages = visiblePages(survey).filter((p) => !isReviewName(p?.name));
  if (pages.length === 0) return 0;
  const completed = pages.filter((page) => !page?.hasErrors?.(false)).length;
  return Math.round((completed / pages.length) * 100);
}

function canNavigateTo(survey, index, validatedStepIndices) {
  const pages = visiblePages(survey);
  const current = pages.indexOf(survey.currentPage);

  if (index < current) return true;

  const previousPageIsValid = !survey.currentPage.hasErrors(true);
  if (Number.isFinite(current) && current >= 0) validatedStepIndices?.add?.(current + 1);
  const targetPage = pages[index];
  if (!targetPage) return false;
  const targetPageCompleted = !targetPage.hasErrors(false);

  return targetPageCompleted || (index === current + 1 && previousPageIsValid);
}

function visiblePages(survey) {
  return Array.isArray(survey?.visiblePages) ? survey.visiblePages : [];
}

function isReviewName(name) {
  return name === REVIEW_PAGE_NAME;
}

function pageTitle(page) {
  const raw = page?.name;
  if (typeof raw === "string" && raw.trim().length > 0) return raw;
  return UNNAMED_PAGE_FALLBACK_TITLE;
}
